import React, { useState, useEffect } from 'react';
import { Property, AppUser } from '../../types';
import { DashboardResultsHeader } from './dashboard/DashboardResultsHeader';
import { SelectionModeHeader } from './dashboard/SelectionModeHeader';
import { FloatingActionMenu } from './dashboard/FloatingActionMenu';
import { BrokerModalsContainer } from './dashboard/BrokerModalsContainer';
import { BrokerResultsViewContent } from './dashboard/BrokerResultsViewContent';
import { useBulkSelection } from '../../hooks/useBulkSelection';
import { useClientExportPayload } from '../../hooks/useClientExportPayload';

interface BrokerResultsContainerProps {
  propertiesData: any;
  appUser: AppUser | null;
  searchMode: 'ai' | 'code' | 'map';
  spatialFilter: any;
  onSpatialFilterChange: (filter: any) => void;
  isSelectionMode: boolean;
  setIsSelectionMode: (value: boolean) => void;
  inmobiliariasConfig: Record<string, any>;
  viewMode: 'grid' | 'map';
  setViewMode: (mode: 'grid' | 'map') => void;
  filterInput?: any;
}

export const BrokerResultsContainer: React.FC<BrokerResultsContainerProps> = ({
  propertiesData,
  appUser,
  searchMode,
  spatialFilter,
  onSpatialFilterChange,
  isSelectionMode,
  setIsSelectionMode,
  inmobiliariasConfig,
  viewMode,
  setViewMode,
  filterInput,
}) => {
  const [selectedPropertyDetail, setSelectedPropertyDetail] = useState<Property | null>(null);
  const [propertyToShare, setPropertyToShare] = useState<Property | null>(null);
  const [interestedProperty, setInterestedProperty] = useState<Property | null>(null);
  const [isMassShareModalOpen, setIsMassShareModalOpen] = useState(false);

  const bulkSelection = useBulkSelection(propertiesData.totalMatchingPropertiesCount);

  useEffect(() => {
    if (isSelectionMode && viewMode !== 'grid') setViewMode('grid');
  }, [isSelectionMode, viewMode, setViewMode]);

  const handleCancelSelection = () => {
    setIsSelectionMode(false);
    bulkSelection.clearSelection();
  };

  const currentSelectionPayload = useClientExportPayload({
    appUser,
    bulkSelection,
    filterInput: filterInput || {},
    totalMatchingPropertiesCount: propertiesData.totalMatchingPropertiesCount,
  });

  return (
    <>
      <section id="results" className={`${isSelectionMode ? 'mt-24' : 'bg-white p-3 sm:p-6 shadow-xl rounded-2xl border border-gray-50'} relative min-h-[400px]`}>
        <DashboardResultsHeader
          totalCount={propertiesData.totalMatchingPropertiesCount}
          viewMode={viewMode}
          setViewMode={setViewMode}
          searchMode={searchMode}
          isLoading={propertiesData.isLoadingProperties}
          isSelectionMode={isSelectionMode}
        />

        {isSelectionMode && (
          <SelectionModeHeader
            selectedCount={bulkSelection.selectedCount}
            totalCount={propertiesData.totalMatchingPropertiesCount}
            onCancel={handleCancelSelection}
          />
        )}

        <BrokerResultsViewContent
          viewMode={viewMode}
          isSelectionMode={isSelectionMode}
          propertiesData={propertiesData}
          bulkSelection={bulkSelection}
          appUser={appUser}
          spatialFilter={spatialFilter}
          onSpatialFilterChange={onSpatialFilterChange}
          inmobiliariasConfig={inmobiliariasConfig}
          filterInput={filterInput}
          onSelectPropertyDetail={setSelectedPropertyDetail}
          onSelectPropertyToShare={setPropertyToShare}
          onSelectInterested={setInterestedProperty}
        />
      </section>

      <FloatingActionMenu
        selectedCount={bulkSelection.selectedCount}
        isSelectionMode={isSelectionMode}
        onClick={() => {
          if (!isSelectionMode) setIsSelectionMode(true);
          else if (bulkSelection.selectedCount > 0) setIsMassShareModalOpen(true);
        }}
      />

      <BrokerModalsContainer
        selectedPropertyDetail={selectedPropertyDetail}
        onClosePropertyDetail={() => setSelectedPropertyDetail(null)}
        propertyToShare={propertyToShare}
        onCloseShareProperty={() => setPropertyToShare(null)}
        interestedProperty={interestedProperty}
        onCloseInterested={() => setInterestedProperty(null)}
        isMassShareModalOpen={isMassShareModalOpen}
        onCloseMassShare={() => setIsMassShareModalOpen(false)}
        selectionPayload={currentSelectionPayload}
        selectedCount={bulkSelection.selectedCount}
        appUser={appUser}
        inmobiliariasConfig={inmobiliariasConfig}
        updatePropertyLocally={(id, u) => propertiesData.updatePropertyLocally(id, u)}
        onViewProperty={setSelectedPropertyDetail}
        onShareProperty={setPropertyToShare}
      />
    </>
  );
};
